// ./src/components/features/EventSetup/TestRecordsDrawer.tsx
//
// ROADMAP #188 — the event's TEST records, and Clear. Owner, 2026-10-21: everything a test run makes on an event is test;
// once the event is live they are hidden from every list, count and report, shown on request, cleared any time.
//
// ⚠ READ FRESH EVERY TIME IT OPENS (`eventTestRecords`): while the event is in test mode, the current run's records (and
//   how many of its test places are used); once it is live, the HIDDEN ones. A failed read is an error, never "none".
// ⚠ CLEAR CANNOT BE UNDONE: the drawer shows what goes, and its red button is the clear (no ⌘S, no second question).
// ⓘ A drawer, not ConfirmModal: it carries a read the host decides on (house rule #115). Opened as a URL stage by the
//   dashboard (test mode) and by Details & Settings (either mode); each page owns its stage and its right
//   (`canEditEventSettings` — the test-mode switch's).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Divider, Group, Loader, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { OpResult } from "@sdflc/api-helpers";

import { AppDrawer } from "@/components/generic/AppDrawer";
import { ErrorAlert } from "@/components/generic/ErrorAlert";
import { useToast } from "@/hooks";
import { EventService, getGuestRoleLabel, getRsvpStatusLabel } from "@/utils/api";
import type { EventTestRecords } from "@/utils/api";
import { formatCurrency, getFormErrors } from "@/utils";

import { testRecordTotal } from "./setupStepRules";

/** The clear has no field of its own: every refusal is said under the drawer (none is swallowed). */
const KNOWN_FORM_FIELDS = [] as const;

export interface TestRecordsDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /** After a clear — the page re-reads what it shows. */
  onCleared?: (left: EventTestRecords | null) => void;
}

const TestRecordsDrawerComponent: React.FC<TestRecordsDrawerProps> = ({ opened, onClose, eventId, onCleared }) => {
  const { t } = useTranslation(["events", "common"]);
  const toast = useToast();
  const [records, setRecords] = useState<EventTestRecords | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState<OpResult | null>(null);

  // Only the latest read may land: one still in flight from an earlier open is dropped.
  const request = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++request.current;
    setLoading(true);
    setLoadError(null);

    const result = await EventService.testRecords(eventId);

    if (mine !== request.current) {
      return;
    }

    setLoading(false);

    if (!result.didSucceed()) {
      setRecords(null);
      setLoadError(result);

      return;
    }

    setRecords((result.getDataFirst() as EventTestRecords | undefined) ?? null);
  }, [eventId]);

  useEffect(() => {
    if (!opened) {
      request.current += 1;
      setLoading(false);

      return;
    }

    setClearError(null);
    void load();
  }, [opened, load]);

  const total = testRecordTotal(records);
  const { genericErrors } = getFormErrors(clearError, KNOWN_FORM_FIELDS);

  const handleClear = useCallback(async () => {
    setClearing(true);
    setClearError(null);

    const result = await EventService.clearTestRecords(eventId);

    setClearing(false);

    if (!result.didSucceed()) {
      setClearError(result);

      return;
    }

    // ⓘ A bulk action: no row to highlight, so the count is the confirmation.
    toast.success(t("events:testRecords.cleared", { defaultValue: "Cleared {{count}} test record(s).", count: total }));
    onCleared?.((result.getDataFirst() as EventTestRecords | undefined) ?? null);
    onClose();
  }, [eventId, onCleared, onClose, t, toast, total]);

  const counted = records
    ? [
        { key: "guests", n: records.guestCount, label: t("events:testRecords.guests", { defaultValue: "Guests" }) },
        { key: "orders", n: records.orderCount, label: t("events:testRecords.orders", { defaultValue: "Ticket orders" }) },
        { key: "tickets", n: records.ticketCount, label: t("events:testRecords.tickets", { defaultValue: "Tickets" }) },
        { key: "signups", n: records.signupOrderCount, label: t("events:testRecords.signupOrders", { defaultValue: "Sign-up orders" }) },
        { key: "applications", n: records.applicationCount, label: t("events:testRecords.applications", { defaultValue: "Sponsorship applications" }) },
        { key: "payments", n: records.paymentCount, label: t("events:testRecords.payments", { defaultValue: "Test payments" }) },
      ].filter((row) => row.n > 0)
    : [];

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("events:testRecords.title", { defaultValue: "Test records" })}
      size="md"
      closable={!clearing}
      // ⓘ A viewer (Close) with ONE action beside it: nothing is typed here, so there is nothing to ask about on Esc.
      actionType="close"
      cancelLabel={t("common:buttons.close", { defaultValue: "Close" })}
      leftActions={[
        {
          label: t("events:testRecords.clear", { defaultValue: "Clear test data" }),
          variant: "danger",
          iconName: "Trash",
          onClick: () => void handleClear(),
          loading: clearing,
          disabled: loading || !records || total === 0,
        },
      ]}
      loading={clearing}
      errors={genericErrors.length > 0 ? genericErrors : null}
    >
      <Stack gap="md">
        {loading && (
          <Group gap="xs">
            <Loader size="sm" />
            <Text size="md" c="dimmed">
              {t("events:testRecords.loading", { defaultValue: "Reading the test records…" })}
            </Text>
          </Group>
        )}

        {loadError && <ErrorAlert error={loadError} title={t("events:testRecords.loadFailed", { defaultValue: "We could not read the test records" })} page="Test records" operation="eventTestRecords" />}

        {records && !loading && (
          <Stack gap="sm">
            <Text size="md">
              {records.hidden
                ? t("events:testRecords.hiddenIntro", {
                    defaultValue: "Made while this event was in test mode. They are hidden from every list, count and report — none of them counts.",
                  })
                : t("events:testRecords.liveIntro", {
                    defaultValue: "Made while this event is in test mode. They count like real ones while you test; once the event is live they are hidden from every list, count and report.",
                  })}
            </Text>

            {!records.hidden && (
              <Text size="md" fw={600}>
                {t("events:testRecords.places", {
                  defaultValue: "{{used}} of {{limit}} test places used.",
                  used: records.placesUsed,
                  limit: records.limit,
                })}
              </Text>
            )}

            {total === 0 ? (
              <Text size="md" c="dimmed">
                {records.hidden
                  ? t("events:testRecords.noneHidden", { defaultValue: "No test records are hidden." })
                  : t("events:testRecords.none", { defaultValue: "No test records yet." })}
              </Text>
            ) : (
              <>
                <Stack gap={4}>
                  {counted.map((row) => (
                    <Group key={row.key} justify="space-between" wrap="nowrap" gap="sm">
                      <Text size="md">{row.label}</Text>
                      <Text size="md" fw={600}>
                        {row.n}
                      </Text>
                    </Group>
                  ))}
                </Stack>

                {records.guests.length > 0 && (
                  <>
                    <Divider label={t("events:testRecords.guests", { defaultValue: "Guests" })} labelPosition="left" />
                    <Stack gap={4}>
                      {records.guests.map((g) => (
                        <Group key={g.id} justify="space-between" wrap="nowrap" gap="sm">
                          <Text size="md" truncate>
                            {g.name || t("events:testRecords.unnamed", { defaultValue: "Unnamed guest" })}
                          </Text>
                          <Text size="md" c="dimmed" style={{ flexShrink: 0 }}>
                            {[getGuestRoleLabel(g.guestRole), getRsvpStatusLabel(g.rsvpStatus)].join(" · ")}
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                  </>
                )}

                {records.orders.length > 0 && (
                  <>
                    <Divider label={t("events:testRecords.orders", { defaultValue: "Ticket orders" })} labelPosition="left" />
                    <Stack gap={4}>
                      {records.orders.map((o) => (
                        <Group key={o.id} justify="space-between" wrap="nowrap" gap="sm">
                          <Text size="md" truncate>
                            {[o.referenceCode, t("events:testRecords.ticketCount", { defaultValue: "{{count}} ticket(s)", count: o.quantity ?? 0 })]
                              .filter(Boolean)
                              .join(" · ")}
                          </Text>
                          <Text size="md" fw={600} style={{ flexShrink: 0 }}>
                            {formatCurrency(Number(o.totalAmount) || 0, o.currencyCode || "USD")}
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                  </>
                )}

                {(records.guestCount > records.guests.length || records.orderCount > records.orders.length) && (
                  <Text size="md" c="dimmed">
                    {t("events:testRecords.newestOnly", {
                      defaultValue: "The newest {{count}} of each are listed.",
                      count: Math.max(records.guests.length, records.orders.length),
                    })}
                  </Text>
                )}

                <Text size="md" c="dimmed">
                  {records.hidden
                    ? t("events:testRecords.clearHintHidden", { defaultValue: "Clearing removes them for good." })
                    : t("events:testRecords.clearHint", {
                        defaultValue:
                          "Clearing removes them for good — and any hidden from earlier test runs: their places come back, and you can test again from zero. Stripe test payments are not refunded — no real money moved.",
                      })}
                </Text>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </AppDrawer>
  );
};

export const TestRecordsDrawer = React.memo(TestRecordsDrawerComponent);
